import React from "react";

const InnerContainer = ({
  children,
  isoncompareproducts,
}: {
  children: React.ReactNode;
  isoncompareproducts?: boolean;
}) => {
  return (
    <span
      className={`block transform skew-x-15 skew-y-0 hover:no-underline ${
        isoncompareproducts ? "pr-2 pl-3 py-1/2" : ""
      }`}
    >
      {children}
    </span>
  );
};

const bgColors = {
  lightBlue: "bg-blue-500 font-normal text-shadow-200",
  orange: "bg-orange-600 font-normal text-shadow-200",
  blue: "bg-blue-800 font-normal text-shadow-200",
  darkBlue: "bg-blue-800 font-normal text-shadow-200",
  brown: "bg-goodBrown-100 text-black font-semibold",
  gray: "bg-betterGray-100 text-black font-semibold",
  darkGray: "bg-gray-600 font-normal text-shadow-200",
  yellow: "bg-bestYellow-100 text-black font-semibold",
  green: "bg-green-500 font-normal text-shadow-200",
  lightRed: "bg-red-500 font-normal text-shadow-200",
  darkRed: "bg-red-800 font-normal text-shadow-200",
  rewardsBlue: "bg-rewardsBlue-300 font-normal text-shadow-200",
};

const bgHoverColor = {
  lightBlue: "hover:bg-blue-500",
  orange: "hover:bg-orange-600",
  blue: "hover:bg-blue-900",
  darkBlue: "hover:bg-blue-900",
  brown: "hover:bg-goodBrown-200",
  gray: "hover:bg-betterGray-200",
  darkGray: "hover:bg-gray-700",
  yellow: "hover:bg-bestYellow-200",
  green: "hover:bg-green-600",
  lightRed: "hover:bg-red-500",
  darkRed: "hover:bg-red-800",
  rewardsBlue: "hover:bg-rewardsBlue-300",
};

export type RibbonProps = {
  className?: string;
  children: React.ReactNode;
  bgcolor: keyof typeof bgColors;
  squareLeftSide?: boolean;
  anchorRef?: React.RefObject<HTMLAnchorElement>;
  ariaExpanded?: boolean;
  isoncompareproducts?: boolean;
};

type AnchorProps = RibbonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "type">;

type ButtonProps = RibbonProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

type DivProps = RibbonProps &
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

function Ribbon(props: ButtonProps): JSX.Element;
function Ribbon(props: DivProps): JSX.Element;
function Ribbon(props: AnchorProps): JSX.Element;

function Ribbon(props: ButtonProps | DivProps | AnchorProps) {
  const {
    children,
    className = "",
    style = {},
    bgcolor,
    squareLeftSide,
    anchorRef,
    isoncompareproducts,
    ...forwardedProps
  } = props;

  const baseClasses =
    "rounded-tl rounded-br rounded-tr-xs rounded-bl-xs  text-xs leading-3-1/2 py-1/2 align-middle transform -skew-x-15 skew-y-0  w-auto text-white";

  const bgColorClass = bgColors[bgcolor];
  const hoverClass = bgHoverColor[bgcolor];

  const classes = `${baseClasses} ${bgColorClass} ${className} ${
    squareLeftSide ? "mr-1-1/2 -ml-2 pr-2 pl-3" : "mr-1-1/2 py-2"
  } `;

  if ("href" in props) {
    return (
      <a
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        {...(forwardedProps as AnchorProps)}
        className={`${classes} ${hoverClass} ${"cursor-pointer px-1"}`}
        style={style}
        ref={anchorRef}
      >
        <InnerContainer>{children}</InnerContainer>
      </a>
    );
  }

  if ("type" in props) {
    return (
      // eslint-disable-next-line react/button-has-type
      <button
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        {...(forwardedProps as ButtonProps)}
        className={`${classes} ${hoverClass} ${"border-0 cursor-pointer mb-0"}`}
        style={style}
      >
        <InnerContainer isoncompareproducts={isoncompareproducts}>
          {children}
        </InnerContainer>
      </button>
    );
  }

  return (
    <div
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      {...(forwardedProps as DivProps)}
      className={`${classes}  ${bgColorClass} ${!squareLeftSide ? "px-2" : ""}`}
      style={style}
    >
      <InnerContainer isoncompareproducts={isoncompareproducts}>
        {children}
      </InnerContainer>
    </div>
  );
}

export default Ribbon;
